import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import {
  Article,
  UpdateStatusPayload,
  AddNotePayload,
  UserInfo,
  PersistentNote,
  EmbeddedAnalysisResult,
} from "@/types/canonical";
import AnalysisEntryDetailDrawer from "./AnalysisEntryDetailDrawer";

// Helper function to construct the full avatar URL
const constructAvatarUrl = (
  apiBaseUrl: string,
  relativePath?: string | null,
): string | undefined => {
  if (!relativePath) return undefined;

  // If it's already an absolute URL (though backend should provide relative)
  if (relativePath.match(/^https?:\/\//)) {
    return relativePath;
  }

  // Derive the base URL for static assets by removing /api suffix and trailing slash
  const staticBaseUrl = apiBaseUrl.replace(/(\/api)?\/?$/, "");

  // Ensure no double slashes if relativePath starts with /
  const cleanRelativePath = relativePath.startsWith("/")
    ? relativePath.substring(1)
    : relativePath;
  return `${staticBaseUrl}/${cleanRelativePath}`;
};

// Helper function to get status color based on status string
const getStatusColor = (status: string | null) => {
  switch (status?.toLowerCase()) {
    case "open":
      return "bg-primary text-primary-content";
    case "needs review":
      return "bg-secondary text-secondary-content";
    case "in progress":
      return "bg-secondary text-secondary-content";
    case "outdated":
      return "bg-warning text-warning-content";
    case "completed":
      return "bg-success text-success-content";
    case "n/a":
      return "bg-success text-success-content";
    default:
      return "bg-base-content/5 text-base-content/70"; // Use a subtle default
  }
};

interface ArticleDetailPageProps {
  apiBaseUrl: string;
}

const ArticleDetailPage: React.FC<ArticleDetailPageProps> = ({
  apiBaseUrl,
}) => {
  const { articleId } = useParams<{ articleId: string }>();
  const { token } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [analysisDetails, setAnalysisDetails] = useState<
    EmbeddedAnalysisResult[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompleteAIArticle, setShowCompleteAIArticle] = useState(false);
  const [selectedEntry, setSelectedEntry] =
    useState<EmbeddedAnalysisResult | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // State for user interactions
  const [noteInput, setNoteInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { user: currentUser } = useAuth(); // Get current user info

  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false); // New state for horizontal menu

  const statusOptions = useMemo(() => {
    return ["Open", "Needs Review", "In Progress", "Outdated", "Completed", "N/A"].filter(
      (status) => status !== selectedStatus,
    );
  }, [selectedStatus]);

  // Refs for click-outside-to-close functionality
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const statusToggleButtonRef = useRef<HTMLDivElement>(null);

  const handleNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNoteInput(e.target.value);
    },
    [],
  );

  const handleOpenDrawer = useCallback((entry: EmbeddedAnalysisResult) => {
    setSelectedEntry(entry);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setIsStatusMenuOpen(false); // Close status menu if drawer closes
  }, []);

  const fetchData = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError(null);
      if (!articleId) {
        setError("Article ID is missing from URL.");
        setLoading(false);
        return;
      }
      if (!token) {
        setError("Authentication token not found. Please login.");
        setLoading(false);
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [articleResponse, analysisResponse] = await Promise.all([
          axios.get<Article>(`${apiBaseUrl}/canonical/articles/${articleId}`, {
            headers,
          }),
          axios.get<EmbeddedAnalysisResult[]>(
            `${apiBaseUrl}/canonical/articles/${articleId}/analysis-details`,
            { headers },
          ),
        ]);

        setArticle(articleResponse.data);

        const analysisWithComments = analysisResponse.data.map((entry) => ({
          ...entry,
          comments: articleResponse.data.persistent_notes
            ?.filter(
              (note: PersistentNote) =>
                note.release_note_id === entry.release_note_id &&
                note.release_item_title === entry.release_item_title,
            )
            .sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime(),
            ),
        }));

        setAnalysisDetails(analysisWithComments);
        if (articleResponse.data.persistent_status) {
          setSelectedStatus(articleResponse.data.persistent_status);
        }
      } catch (err: any) {
        console.error("Error fetching article details:", err);
        let errorMsg = "Failed to fetch article details";
        if (axios.isAxiosError(err) && err.response) {
          errorMsg = err.response.data?.detail || err.message;
        } else if (err instanceof Error) {
          errorMsg = err.message;
        }
        setError(errorMsg);
        setArticle(null);
        setAnalysisDetails(null);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [articleId, token, apiBaseUrl],
  );

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (selectedEntry && analysisDetails) {
      const updatedEntry = analysisDetails.find(
        (entry) => entry.item_id === selectedEntry.item_id,
      );
      if (updatedEntry) {
        if (JSON.stringify(updatedEntry) !== JSON.stringify(selectedEntry)) {
          setSelectedEntry(updatedEntry);
        }
      } else {
        console.warn(
          `Selected entry with ID ${selectedEntry.item_id} not found in updated analysis details. Closing drawer.`,
        );
        handleCloseDrawer();
      }
    }
  }, [analysisDetails, selectedEntry?.item_id, handleCloseDrawer]);

  const handleAddNote = async () => {
    if (!noteInput.trim() || !articleId || !token) return;
    setIsSubmittingNote(true);
    setError(null);
    try {
      const payload: AddNotePayload = { text: noteInput.trim() };
      const response = await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/notes`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArticle(response.data);
      setNoteInput("");
      await fetchData(false);
    } catch (err: any) {
      console.error("Error adding note:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to add note.";
      setError(errorMsg);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!status || !articleId || !token) return;
    setIsSubmittingStatus(true);
    setError(null);
    try {
      const payload: UpdateStatusPayload = { status: status };
      const response = await axios.put<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArticle(response.data);
      setSelectedStatus(status);
      setIsStatusMenuOpen(false);
      await fetchData(false);
    } catch (err: any) {
      console.error("Error updating status:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to update status.";
      setError(errorMsg);
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const handleAssignArticle = async () => {
    if (!articleId || !token) return;
    setIsAssigning(true);
    setError(null);
    try {
      const response = await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/assign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArticle(response.data);
      await fetchData(false);
    } catch (err: any) {
      console.error("Error assigning article:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to assign article.";
      setError(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignArticle = async () => {
    if (!articleId || !token) return;
    setIsAssigning(true);
    setError(null);
    try {
      const response = await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/unassign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArticle(response.data);
      await fetchData(false);
    } catch (err: any) {
      console.error("Error unassigning article:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to unassign article.";
      setError(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  // Skeleton Loader using daisyUI
  if (loading) {
    return (
      <div className="container mx-auto space-y-6 py-6">
        <div className="h-8 w-3/4 skeleton"></div>
        <div className="h-4 w-1/2 skeleton"></div>
        <div className="mb-6 h-4 w-1/4 skeleton"></div>
        <div className="card bg-base-200 shadow-xl">
          <div className="card-body">
            <div className="mb-2 h-6 w-1/4 skeleton"></div>
            <div className="h-20 w-full skeleton"></div>
          </div>
        </div>
        <div className="card bg-base-200 shadow-xl">
          <div className="card-body">
            <div className="mb-2 h-6 w-1/4 skeleton"></div>
            <div className="h-32 w-full skeleton"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error display using daisyUI alert
  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div role="alert" className="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">Error</h3>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return <div className="container mx-auto py-6">Article not found.</div>;
  }

  const handleShowCompleteAIArticle = () => {
    setShowCompleteAIArticle(!showCompleteAIArticle);
  };

  // Helper to render analysis details using daisyUI Card
  const renderAnalysisEntry = (
    entry: EmbeddedAnalysisResult & { comments?: PersistentNote[] },
  ) => {
    const isResolved =
      entry.resolution_status === "resolved" ||
      entry.resolution_status === "declined";
    const effectiveUpdateNeeded = isResolved ? false : entry.update_needed;

    const getEffectiveStatusColor = () => {
      if (effectiveUpdateNeeded === true) return "badge-error";
      if (effectiveUpdateNeeded === false) return "badge-success";
      return "badge-ghost";
    };
    const getEffectiveStatusText = () => {
      if (effectiveUpdateNeeded === true) return "Updates needed";
      if (effectiveUpdateNeeded === false) return "Completed";
      return "N/A";
    };

    return (
      <div
        key={entry.item_id}
        className="card-bordered card cursor-pointer bg-base-200 p-5 drop-shadow-lg transition-shadow hover:shadow-lg"
        onClick={() => handleOpenDrawer(entry)}
      >
        <div className="card-body p-4">
          <h2 className="card-title text-sm text-base-content">
            {entry.release_item_title}
          </h2>
          {entry.release_note_version && (
            <span className="text-xs text-base-content opacity-60">
              {entry.release_note_version}
            </span>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <div
              className={`badge font-mono text-xs font-bold shadow-md ${getEffectiveStatusColor()}`}
            >
              {getEffectiveStatusText()}
            </div>
            {isResolved && (
              <div
                className={`badge font-mono text-xs shadow-md ${
                  entry.resolution_status === "resolved"
                    ? "badge-info"
                    : "badge-accent"
                }`}
              >
                {entry.resolution_status === "resolved"
                  ? "Resolved"
                  : "Declined"}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get user display name (can be moved to utils if shared more widely)
  const getUserDisplayName = (userInfo?: UserInfo | null): string => {
    if (!userInfo) return "Unknown User";
    return userInfo.name || userInfo.username;
  };

  // Helper function to get avatar fallback (can be moved to utils)
  const getAvatarFallback = (userInfo?: UserInfo | null): string => {
    if (!userInfo) return "UU";
    const name = userInfo.name;
    if (name) {
      const parts = name.split(" ");
      if (parts.length > 1) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return userInfo.username?.substring(0, 2).toUpperCase() ?? "??";
  };

  const handleToggleStatusMenu = () => {
    setIsStatusMenuOpen(!isStatusMenuOpen);
  };

  return (
    <div className="container mx-auto max-w-screen-lg space-y-6 py-6">
      {/* Header */}
      <div className="flex justify-between gap-2">
        <div>
          <h1 className="mb-2 bg-gradient-to-r from-base-content/80 to-base-content bg-clip-text text-2xl font-bold text-transparent">
            {article.title}
          </h1>
          <p className="mb-1 text-sm text-base-content opacity-60">
            Article ID: {article._id}
          </p>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="link text-sm link-info link-hover"
            >
              View Live Article <span className="align-super text-xs">↗</span>
            </a>
          )}
        </div>

        <div className="flex flex-col items-end gap-4">
          <div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex items-center">
                  <div
                    ref={statusToggleButtonRef}
                    role="button"
                    tabIndex={0}
                    className={`badge border-none font-mono text-xs font-semibold shadow-lg ${getStatusColor(
                      selectedStatus || null,
                    )} cursor-pointer transition-all duration-300 ease-in-out select-none`}
                    onClick={handleToggleStatusMenu}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleToggleStatusMenu()
                    }
                  >
                    <span>{selectedStatus || "Set Status..."}</span>
                  </div>
                  <div
                    ref={statusMenuRef}
                    className={`group absolute right-full mr-2 flex transform items-center transition-all duration-500 ease-in-out ${
                      isStatusMenuOpen
                        ? "translate-x-0 opacity-100"
                        : "pointer-events-none translate-x-4 opacity-0"
                    }`}
                  >
                    {statusOptions.map((status, index) => (
                      <button
                        key={status}
                        className={`badge ${getStatusColor(
                          status,
                        )} ml-1 w-max cursor-pointer border-none font-mono text-xs transition-all duration-300 ease-in-out ${
                          isStatusMenuOpen
                            ? "opacity-100 group-hover:text-xs hover:text-xs"
                            : "opacity-0"
                        }`}
                        style={{
                          transitionDelay: isStatusMenuOpen
                            ? `${(statusOptions.length - 1 - index) * 100}ms`
                            : "0ms",
                          transform: isStatusMenuOpen
                            ? "translateX(0)"
                            : "translateX(20px)",
                        }}
                        onClick={() => {
                          if (!isSubmittingStatus) {
                            handleUpdateStatus(status);
                          }
                        }}
                        disabled={isSubmittingStatus}
                      >
                        {isSubmittingStatus && selectedStatus === status ? (
                          <span className="loading loading-xs loading-spinner"></span>
                        ) : (
                          status
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex flex-col items-start">
              {article.assigned_to ? (
                <div className="flex items-center justify-between">
                  <div className="group relative">
                    <div className="flex items-center gap-2">
                      <div
                        className={`avatar ${
                          !article.assigned_to.avatar_url ? "placeholder" : ""
                        }`}
                      >
                        <div className="h-4 w-4 rounded-full bg-neutral text-neutral-content">
                          {article.assigned_to.avatar_url ? (
                            <img
                              src={constructAvatarUrl(
                                apiBaseUrl,
                                article.assigned_to.avatar_url,
                              )}
                              alt={getUserDisplayName(article.assigned_to)}
                            />
                          ) : (
                            <span>
                              {getAvatarFallback(article.assigned_to)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">
                          {getUserDisplayName(article.assigned_to)}
                        </p>
                        <p className="text-xs text-base-content opacity-60">
                          {article.assigned_to.username}
                        </p>
                      </div>
                    </div>
                    {(currentUser?.username === article.assigned_to.username ||
                      currentUser?.superuser) && (
                      <div className="absolute top-full left-0 z-10 mt-2 origin-top scale-95 opacity-0 transition-all delay-150 duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100">
                        <div className="rounded-lg border border-base-300 bg-base-100 p-2 shadow-lg">
                          <button
                            className="btn w-full btn-outline btn-sm btn-warning"
                            onClick={handleUnassignArticle}
                            disabled={isAssigning}
                          >
                            {isAssigning && (
                              <span className="loading loading-xs loading-spinner"></span>
                            )}
                            {isAssigning ? "Unassigning..." : "Unassign"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {currentUser && !article.assigned_to && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={handleAssignArticle}
                      disabled={isAssigning}
                    >
                      {isAssigning && (
                        <span className="loading loading-xs loading-spinner"></span>
                      )}
                      {isAssigning ? "Assigning..." : "Assign to Me"}
                    </button>
                  )}
                  {!currentUser && (
                    <p className="text-sm text-base-content opacity-60">
                      Login to assign.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-bordered card bg-base-100">
        <div className="">
          <div className="card-body">
            <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="card-title">Release Items</h2>
                {article.last_seen_run_id ? (
                  <p className="mt-1 font-mono text-sm text-base-content/70">
                    {" "}
                    {article.db_last_updated
                      ? new Date(article.db_last_updated).toLocaleString(
                          "sv-SE",
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          },
                        )
                      : "N/A"}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-base-content/70">
                    No analysis details found for this article.
                  </p>
                )}
              </div>
              <button
                className="btn mt-2 btn-outline btn-sm sm:mt-0"
                onClick={handleShowCompleteAIArticle}
              >
                {showCompleteAIArticle
                  ? "Show Analysis Details"
                  : "Show Complete AI Article"}
              </button>
            </div>

            {showCompleteAIArticle ? (
              <div>
                <h4 className="mt-4 mb-2 font-semibold">
                  Article Content (with suggested changes):
                </h4>
                {article.body_html_with_diffs ? (
                  <div className="description-image-container article-diff-view prose prose-sm max-w-none text-sm sm:prose-base">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: article.body_html_with_diffs,
                      }}
                    />
                  </div>
                ) : article.body_html ? (
                  <div className="description-image-container prose prose-sm max-w-none text-sm sm:prose-base">
                    <div
                      dangerouslySetInnerHTML={{ __html: article.body_html }}
                    />
                  </div>
                ) : (
                  <p className="mb-4 text-sm text-base-content opacity-60">
                    Article content not available.
                  </p>
                )}
              </div>
            ) : analysisDetails && analysisDetails.length > 0 ? (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-2">
                {analysisDetails.map(renderAnalysisEntry)}
              </div>
            ) : (
              <p className="mt-4 text-sm text-base-content opacity-60">
                No analysis details available to display.
              </p>
            )}
          </div>
        </div>

        {selectedEntry && (
          <AnalysisEntryDetailDrawer
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            entry={selectedEntry}
            articleId={article._id}
            apiBaseUrl={apiBaseUrl}
            fetchData={fetchData}
          />
        )}

        <div className="card-bordered card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Comments & Notes</h2>
            <div className="pr-2">
              {(() => {
                const allComments = [
                  ...(article?.persistent_notes || [])
                    .filter(
                      (note: PersistentNote) =>
                        !note.release_item_title && !note.release_note_id,
                    )
                    .map((note: PersistentNote) => ({
                      ...note,
                      type: "article" as const,
                    })),
                  ...(analysisDetails || []).flatMap((entry) =>
                    (
                      (
                        entry as EmbeddedAnalysisResult & {
                          comments?: PersistentNote[];
                        }
                      ).comments || []
                    ).map((comment: PersistentNote) => ({
                      ...comment,
                      type: "release_item" as const,
                      release_item_title: entry.release_item_title,
                    })),
                  ),
                ];

                allComments.sort(
                  (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime(),
                );

                if (allComments.length === 0) {
                  return (
                    <p className="text-sm text-base-content opacity-60">
                      No comments or notes added yet.
                    </p>
                  );
                }

                return (
                  <ul className="timeline timeline-vertical timeline-compact">
                    {allComments.map((comment, index, arr) => (
                      <li
                        key={`${(comment as any)._id || "comment"}-${index}-${comment.timestamp}`}
                      >
                        {index !== 0 && <hr className="my-1 bg-base-300/50" />}
                        <div className="timeline-middle px-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="h-4 w-4 text-primary"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="timeline-start p-2 text-sm">
                          <p className="mb-1 break-words whitespace-normal">
                            {comment.text}
                          </p>
                          <div className="flex flex-wrap items-center justify-end gap-x-1 gap-y-0.5 text-xs text-base-content opacity-80">
                            {comment.user && (
                              <div
                                className={`avatar-xs avatar ${
                                  !comment.user.avatar_url ? "placeholder" : ""
                                }`}
                              >
                                <div className="h-4 w-4 rounded-full bg-neutral text-neutral-content">
                                  {comment.user.avatar_url ? (
                                    <img
                                      src={constructAvatarUrl(
                                        apiBaseUrl,
                                        comment.user.avatar_url,
                                      )}
                                      alt={getUserDisplayName(comment.user)}
                                    />
                                  ) : (
                                    <span>
                                      {getAvatarFallback(comment.user)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            <span>{getUserDisplayName(comment.user)}</span>
                            <time className="whitespace-nowrap">
                              {new Date(comment.timestamp).toLocaleString(
                                "sv-SE",
                                {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </time>
                            {(comment as any).type === "release_item" &&
                              comment.release_item_title && (
                                <div
                                  className="badge-outline ml-1 badge truncate badge-xs badge-info"
                                  title={comment.release_item_title}
                                >
                                  {comment.release_item_title}
                                </div>
                              )}
                          </div>
                        </div>
                        {index !== arr.length - 1 && (
                          <hr className="my-1 bg-base-300/50" />
                        )}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>

            <div className="divider"></div>
            <div className="space-y-2">
              <textarea
                className="textarea w-full textarea-ghost transition-all duration-200 focus:outline-none"
                placeholder="Add a new note..."
                value={noteInput}
                onChange={handleNoteChange}
                rows={3}
              />
              <button
                className="btn btn-sm btn-primary"
                onClick={handleAddNote}
                disabled={isSubmittingNote || !noteInput.trim()}
              >
                {isSubmittingNote && (
                  <span className="loading loading-xs loading-spinner"></span>
                )}
                {isSubmittingNote ? "Adding..." : "Add Note"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
